//! Speech recognition
// Speech recognition services
export const initializeSpeechRecognition = (language, waitingForStart, isRecording, interviewState) => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SR = window.webkitSpeechRecognition || window.SpeechRecognition;
        const recog = new SR();
        recog.continuous = true;
        recog.interimResults = true;
        recog.lang = language;

        return recog;
    }
    return null;
};

export const setupRecognitionCallbacks = (
    recog,
    waitingForStart,
    setWaitingForStart,
    setInterviewState,
    askQuestion,
    setTranscript,
    setLiveTranscript,
    isRecording,
    interviewState,
    transcript,
    handleSubmitAnswer,
    analyzeTranscriptWithBackend,
    lastAnalysisTimeRef
) => {
    const readRef = (maybeRef) => {
        if (maybeRef && typeof maybeRef === 'object' && 'current' in maybeRef) return maybeRef.current;
        return maybeRef;
    };

    const stopSafe = () => {
        try {
            recog.stop();
        } catch (err) {
            // Some browsers throw if already stopped.
            console.error('Error stopping recognition:', err);
        }
    };

    recog.onresult = (e) => {
        let interimText = '';
        let finalText = '';

        for (let i = e.resultIndex; i < e.results.length; i++) {
            const transcript = e.results[i][0].transcript;
            if (e.results[i].isFinal) {
                finalText += transcript + ' ';
            } else {
                interimText += transcript;
            }
        }

        const combinedText = (finalText + interimText).toLowerCase().trim();
        const finalLower = (finalText || '').toLowerCase().trim();

        // 1. Check for start commands when waiting
        if (readRef(waitingForStart)) {
            handleStartCommand(finalLower || combinedText, stopSafe, setWaitingForStart, setInterviewState, askQuestion, setTranscript, setLiveTranscript);
            return;
        }

        // 2. Check for submit commands when recording
        if (readRef(isRecording) && readRef(interviewState) === 'active') {
            const currentTranscript = readRef(transcript) || '';
            if (handleSubmitCommand(finalLower || combinedText, currentTranscript, stopSafe, setTranscript, handleSubmitAnswer)) {
                return;
            }
        }

        // 3. Normal transcription handling
        if (finalText) {
            const currentTranscript = readRef(transcript) || '';
            const newTranscript = (currentTranscript + ' ' + finalText).trim();
            setTranscript(newTranscript);
            setLiveTranscript('');

            // Analyze with backend
            if (readRef(isRecording)) {
                const now = Date.now();
                if (now - lastAnalysisTimeRef.current > 3000) {
                    lastAnalysisTimeRef.current = now;
                    analyzeTranscriptWithBackend(newTranscript);
                }
            }
        } else {
            setLiveTranscript(interimText);
        }
    };

    return recog;
};

const handleStartCommand = (combinedText, stopSafe, setWaitingForStart, setInterviewState, askQuestion, setTranscript, setLiveTranscript) => {
    const startCommands = ['start', 'begin', 'ready', 'go', "let's start", "i'm ready"];
    const hasStartCommand = startCommands.some(cmd =>
        combinedText.includes(cmd)
    );

    if (hasStartCommand) {
        console.log('✅ Start command detected!');
        stopSafe();
        setWaitingForStart(false);
        setTranscript('');
        setLiveTranscript('');
        setTimeout(() => {
            setInterviewState('active');
            askQuestion(0);
        }, 500);
        return true;
    }
    return false;
};

const handleSubmitCommand = (combinedText, currentTranscript, stopSafe, setTranscript, handleSubmitAnswer) => {
    const submitCommands = [
        "submit", "next question", "next", "done", "finished",
        "complete", "okay next", "ok next", "move on", "go to next",
        "that's all", "thats all", "i'm done", "im done", "next one",
        "next please", "i'm finished", "end answer", "stop recording",
        "i'm complete", "that's it", "thats it"
    ];

    // To avoid accidental triggers mid-sentence, prefer commands that appear at the end.
    const normalized = (combinedText || '').replace(/\s+/g, ' ').trim();
    const hasSubmitCommand = submitCommands.some((cmd) => {
        const esc = cmd.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const endRe = new RegExp(`(?:^|\\b)${esc}(?:\\b|$)$`, 'i');
        return endRe.test(normalized) || normalized.endsWith(cmd);
    });

    if (hasSubmitCommand) {
        console.log("✅ Submit command detected:", combinedText);

        // Stop recognition first
        stopSafe();

        // Clean transcript (remove command phrase only if it appears at the end)
        let cleanTranscript = (currentTranscript || '').trim();
        submitCommands.forEach((cmd) => {
            const esc = cmd.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const endRe = new RegExp(`\\s*(?:${esc})\\s*$`, 'i');
            cleanTranscript = cleanTranscript.replace(endRe, '').trim();
        });

        setTranscript(cleanTranscript);

        // Submit answer after a brief delay
        setTimeout(() => {
            handleSubmitAnswer();
        }, 300);
        return true;
    }
    return false;
};